/**
 * Integrytas Group - Base App (MOBILE)
 * Maps csm_page_master.internal_code -> the MOBILE-NATIVE React component that
 * renders the page on the mobile admin app.
 *
 * This is the mobile counterpart of src/pages/page_components.tsx and is the
 * SINGLE source of truth for what the mobile shell can open. Per CLAUDE.md §16
 * the mobile presentation layer is a SEPARATE codebase: entries here resolve to
 * touch-first, mobile-native screens — NOT the desktop PAGE_COMPONENTS re-hosted
 * in a phone viewport. A page with no entry falls back to an explicit
 * "not yet available on mobile" placeholder, never to its desktop component.
 *
 * IMPORTANT: never import from ../pages/page_components here — that module pulls
 * every desktop page into the bundle and would defeat the separation (and bloat
 * the mobile build). Mobile screens share only the data/logic layer (apiDb reads,
 * the Common Routine via routed_writes, RLS, page_registry, RBAC), never the UI.
 */

import type { ComponentType } from 'react';
import type { AuthSession } from '../types/auth';
import { MobileUserListPage } from './pages/MobileUserListPage';
import { MobileRoleListPage } from './pages/MobileRoleListPage';
import { MobileCompanyPage, MobileDivisionPage, MobileBusinessUnitPage, MobileOfficePage } from './pages/MobileOrgLookupPage';
import { MobileDocFormatMaskPage, MobileDocPageSizePage, MobileDocResourcePage } from './pages/MobileDocLookupPages';
import { MobileJobDescriptionPage } from './pages/MobileJobDescriptionPage';
import { MobileSystemParametersPage } from './pages/MobileSystemParametersPage';
import { MobileFxrCurrencyPage, MobileFxrCurrencyRatePage } from './pages/MobileFxrReferencePages';
import { MobileI18nManagerPage } from './pages/MobileI18nManagerPage';
import { MobileDffFlagsPage } from './pages/MobileDffFlagsPage';
import { MobileMyApprovalsPage } from './pages/MobileMyApprovalsPage';
import { MobileApprovalConfigPage } from './pages/MobileApprovalConfigPage';
import { MobileApprovalDelegationsPage } from './pages/MobileApprovalDelegationsPage';
import { MobileApprovalAnalyticsPage } from './pages/MobileApprovalAnalyticsPage';
import { MobileApprovalSlaAuthorityPage } from './pages/MobileApprovalSlaAuthorityPage';
import { MobileApprovalHierarchyPage } from './pages/MobileApprovalHierarchyPage';
import { MobileMtaRuleManagerPage } from './pages/MobileMtaRuleManagerPage';
import { MobileMtaTasksPage } from './pages/MobileMtaTasksPage';
import { MobileUneAdminPage } from './pages/MobileUneAdminPage';
import { MobileUnePreferenceCenterPage } from './pages/MobileUnePreferenceCenterPage';
import { MobileEcmTemplateManagerPage } from './pages/MobileEcmTemplateManagerPage';
import { MobileDocTemplateManagerPage } from './pages/MobileDocTemplateManagerPage';
import { MobileDocGeneratedPage } from './pages/MobileDocGeneratedPage';
import { MobileCmsPageListPage } from './pages/MobileCmsPageListPage';
import { MobileCmsAssetLibraryPage } from './pages/MobileCmsAssetLibraryPage';
import { MobileDmsDocumentsPage } from './pages/MobileDmsDocumentsPage';
import { MobileOlhManagerPage } from './pages/MobileOlhManagerPage';
import { MobileEdsDashboardPage } from './pages/MobileEdsDashboardPage';
import { MobileEdsTemplateListPage } from './pages/MobileEdsTemplateListPage';
import { MobileEdsCampaignListPage } from './pages/MobileEdsCampaignListPage';
import { MobileEdsRecipientPage } from './pages/MobileEdsRecipientPage';
import { MobileEdsEmailLogPage } from './pages/MobileEdsEmailLogPage';
import { MobileEdsOptOutPage } from './pages/MobileEdsOptOutPage';
import { MobileDataQualityRulesPage } from './pages/MobileDataQualityRulesPage';
import { MobileDpcPolicyPage } from './pages/MobileDpcPolicyPage';
import { MobileDpcRequestsPage } from './pages/MobileDpcRequestsPage';
import { MobileDapPolicyManagerPage } from './pages/MobileDapPolicyManagerPage';
import { MobileGtcTrashPage } from './pages/MobileGtcTrashPage';
import { MobileUatTimelinePage } from './pages/MobileUatTimelinePage';
import { MobileAdiAssertionsPage } from './pages/MobileAdiAssertionsPage';
import { MobilePdeDashboardPage } from './pages/MobilePdeDashboardPage';
import { MobileAsfDiagnosticsPage } from './pages/MobileAsfDiagnosticsPage';
import { MobileArlConfigPage } from './pages/MobileArlConfigPage';
import { MobileCmmLocksPage } from './pages/MobileCmmLocksPage';
import { MobileApgTelemetryPage } from './pages/MobileApgTelemetryPage';
import { MobileIsrTargetsPage } from './pages/MobileIsrTargetsPage';
import { MobileGsiIndexerPage } from './pages/MobileGsiIndexerPage';
import { MobileFdmDependencyMapPage } from './pages/MobileFdmDependencyMapPage';
import { MobileCbmBreakersPage } from './pages/MobileCbmBreakersPage';
import { MobileIkeKeysPage } from './pages/MobileIkeKeysPage';
import { MobileRorRoutingPage } from './pages/MobileRorRoutingPage';
import { MobileOdsSyncManagerPage } from './pages/MobileOdsSyncManagerPage';
import { MobileOdsConflictsPage } from './pages/MobileOdsConflictsPage';
import { MobilePfwPortalManagerPage } from './pages/MobilePfwPortalManagerPage';
import { MobilePfwMembershipPage } from './pages/MobilePfwMembershipPage';
import { MobilePfwPortalAdminPage } from './pages/MobilePfwPortalAdminPage';
import { MobileIamProviderManagerPage } from './pages/MobileIamProviderManagerPage';
import { MobileDseRegistryPage } from './pages/MobileDseRegistryPage';
import { MobileDseReportingPage } from './pages/MobileDseReportingPage';
import { MobileDdmRefreshPage } from './pages/MobileDdmRefreshPage';
import { MobileUdcLayoutPage } from './pages/MobileUdcLayoutPage';
import { MobileCdsDatasetListPage } from './pages/MobileCdsDatasetListPage';
import { MobilePheHooksPage } from './pages/MobilePheHooksPage';
import { MobileUldOverridesPage } from './pages/MobileUldOverridesPage';
import { MobileDadExplorerPage } from './pages/MobileDadExplorerPage';
import { MobileFlnFieldSourcePage } from './pages/MobileFlnFieldSourcePage';
import { MobileUppDefinitionManagerPage } from './pages/MobileUppDefinitionManagerPage';
import { MobileLicStatusPage } from './pages/MobileLicStatusPage';
import { MobileReportDesignerPage } from './pages/MobileReportDesignerPage';
import { MobilePageMasterPage } from './pages/MobilePageMasterPage';
import { MobileUserProvisionPage } from './pages/MobileUserProvisionPage';
import { MobileAuditLogPage } from './pages/MobileAuditLogPage';
import { MobileAiConfigPage } from './pages/MobileAiConfigPage';
import { MobileAssetUploadPage } from './pages/MobileAssetUploadPage';
import { MobileBulkImportPage } from './pages/MobileBulkImportPage';
import { MobileI18nNewLanguagePage } from './pages/MobileI18nNewLanguagePage';

export interface MobilePageComponentProps {
  session: AuthSession;
  onBeginMimic?: (userId: number) => void;
}

export type MobilePageComponent = ComponentType<MobilePageComponentProps>;

/**
 * Keyed by the SAME page_internal_code the desktop registry uses, so a page's
 * identity, RBAC (csm_role_page_access) and menu placement stay shared across
 * form factors — only the presentation differs.
 */
export const MOBILE_PAGE_COMPONENTS: Record<string, MobilePageComponent> = {
  'CSM.USER.LIST': MobileUserListPage,
  'CSM.ROLE.LIST': MobileRoleListPage,
  // Organization
  'CSM.ORG.COMPANY': MobileCompanyPage,
  'CSM.ORG.DIVISION': MobileDivisionPage,
  'CSM.ORG.BUSINESS_UNIT': MobileBusinessUnitPage,
  'CSM.ORG.OFFICE': MobileOfficePage,
  // Base tables — document lookups
  'DOC.FORMAT_MASKS': MobileDocFormatMaskPage,
  'DOC.PAGE_SIZES': MobileDocPageSizePage,
  'DOC.RESOURCES': MobileDocResourcePage,
  // Base tables — FX
  'FXR.CURRENCY.LIST': MobileFxrCurrencyPage,
  'FXR.RATE.LIST': MobileFxrCurrencyRatePage,
  // Security & System config
  'CSM.JOB.LIST': MobileJobDescriptionPage,
  'CSM.SYSTEM.PARAMETERS': MobileSystemParametersPage,
  'CSM.I18N.MANAGER': MobileI18nManagerPage,
  'DFF.FLAGS': MobileDffFlagsPage,
  // Workflow / Approvals
  'APR.INBOX': MobileMyApprovalsPage,
  'APR.CONFIG': MobileApprovalConfigPage,
  'APR.DELEGATIONS': MobileApprovalDelegationsPage,
  'APR.ANALYTICS': MobileApprovalAnalyticsPage,
  'APR.SLA_AUTHORITY': MobileApprovalSlaAuthorityPage,
  'CSM.HIERARCHY.LIST': MobileApprovalHierarchyPage,
  'MTA.RULES': MobileMtaRuleManagerPage,
  'MTA.TASKS': MobileMtaTasksPage,
  // Communications
  'UNE.ADMIN': MobileUneAdminPage,
  'UNE.PREFERENCES': MobileUnePreferenceCenterPage,
  'ECM.TEMPLATES': MobileEcmTemplateManagerPage,
  // Content / Documents
  'DOC.TEMPLATES': MobileDocTemplateManagerPage,
  'DOC.GENERATED': MobileDocGeneratedPage,
  'CMS.PAGE.LIST': MobileCmsPageListPage,
  'CMS.ASSET.LIBRARY': MobileCmsAssetLibraryPage,
  'DMS.DOCUMENTS': MobileDmsDocumentsPage,
  'OLH.MANAGER': MobileOlhManagerPage,
  // Email Distribution (EDS)
  'EDS.DASHBOARD': MobileEdsDashboardPage,
  'EDS.TEMPLATE.LIST': MobileEdsTemplateListPage,
  'EDS.CAMPAIGN.LIST': MobileEdsCampaignListPage,
  'EDS.RECIPIENT.LIST': MobileEdsRecipientPage,
  'EDS.EMAIL.LOG': MobileEdsEmailLogPage,
  'EDS.OPT.OUT': MobileEdsOptOutPage,
  // Data Governance
  'DQE.RULES': MobileDataQualityRulesPage,
  'DPC.POLICY': MobileDpcPolicyPage,
  'DPC.REQUESTS': MobileDpcRequestsPage,
  'DAP.POLICIES': MobileDapPolicyManagerPage,
  'GTC.TRASH': MobileGtcTrashPage,
  'UAT.TIMELINE': MobileUatTimelinePage,
  'ADI.WATCHDOG': MobileAdiAssertionsPage,
  // Platform Operations
  'PDE.DASHBOARD': MobilePdeDashboardPage,
  'ASF.DIAGNOSTICS': MobileAsfDiagnosticsPage,
  'ARL.CONFIG': MobileArlConfigPage,
  'CMM.LOCKS': MobileCmmLocksPage,
  'APG.TELEMETRY': MobileApgTelemetryPage,
  'ISR.TARGETS': MobileIsrTargetsPage,
  'GSI.INDEXER': MobileGsiIndexerPage,
  'FDM.MAP': MobileFdmDependencyMapPage,
  'CBM.BREAKERS': MobileCbmBreakersPage,
  'IKE.KEYS': MobileIkeKeysPage,
  'ROR.ROUTING': MobileRorRoutingPage,
  'ODS.SYNC': MobileOdsSyncManagerPage,
  'ODS.CONFLICTS': MobileOdsConflictsPage,
  // Integration — portals & identity
  'PFW.PORTALS': MobilePfwPortalManagerPage,
  'PFW.MEMBERSHIP': MobilePfwMembershipPage,
  'PFW.PORTAL_ADMIN': MobilePfwPortalAdminPage,
  'IAM.PROVIDERS': MobileIamProviderManagerPage,
  // Customization / Low-code
  'DSE.REGISTRY': MobileDseRegistryPage,
  'DSE.REPORTING': MobileDseReportingPage,
  'DDM.REFRESH': MobileDdmRefreshPage,
  'UDC.LAYOUT': MobileUdcLayoutPage,
  'CDS.DATASET.LIST': MobileCdsDatasetListPage,
  'PHE.HOOKS': MobilePheHooksPage,
  'ULD.OVERRIDES': MobileUldOverridesPage,
  'DAD.EXPLORER': MobileDadExplorerPage,
  'FLN.FIELD_SOURCE': MobileFlnFieldSourcePage,
  'UPP.DEFINITIONS': MobileUppDefinitionManagerPage,
  // Licensing & Reporting
  'LIC.STATUS': MobileLicStatusPage,
  'RPD.DESIGNER': MobileReportDesignerPage,
  // Remaining Security & System configuration
  'CSM.PAGE.MASTER': MobilePageMasterPage,
  'CSM.USER.PROVISION': MobileUserProvisionPage,
  'CSM.SYSTEM.AUDIT_LOG': MobileAuditLogPage,
  'CSM.SYSTEM.AI_CONFIG': MobileAiConfigPage,
  'CSM.SYSTEM.ASSETS': MobileAssetUploadPage,
  'CSM.SYSTEM.BULK_IMPORT': MobileBulkImportPage,
  'CSM.I18N.NEW_LANGUAGE': MobileI18nNewLanguagePage,
};
